import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'

export interface AdminToolIngredient {
  id: number
  productId: number
  productName: string
  productNameNumber: string
  categoryId: number | null
  countryId: number
}

interface AdminToolIngredientsState {
  ingredients: AdminToolIngredient[]
  loading: boolean
  updating: boolean
  meta: {
    total: number
    pages: number
  }
  success: string | null
  error: string | null
  stats: {
    totalIngredients: number
    ingredientsWithoutCategory: number
  } | null
}

interface AdminToolIngredientsQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, any>
}

interface AdminToolIngredientsResponse {
  data: AdminToolIngredient[]
  meta: {
    total: number
    pages: number
  }
}

export interface UpdateIngredientMetadataPayload {
  id: number
  data: {
    productName?: string
    categoryId?: number | null
  }
}

const initialState: AdminToolIngredientsState = {
  ingredients: [],
  loading: false,
  updating: false,
  meta: {
    total: 0,
    pages: 0
  },
  success: null,
  error: null,
  stats: null
}

export const fetchAdminToolIngredients = createAsyncThunk<
  AdminToolIngredientsResponse,
  AdminToolIngredientsQueryOptions,
  Redux
>('adminToolsIngredients/fetch', async (queryOptions, { extra }) => {
  const { data } = await extra.httpClient.get('cerebro-product-companies/list', {
    params: queryOptions
  })

  if (Array.isArray(data)) {
    return {
      data,
      meta: {
        total: data.length,
        pages: 1
      }
    }
  }

  if (Array.isArray(data?.data)) {
    return {
      data: data.data,
      meta: data.meta || {
        total: data.data.length,
        pages: 1
      }
    }
  }

  return {
    data: [],
    meta: {
      total: 0,
      pages: 0
    }
  }
})

export const updateAdminToolIngredientMetadata = createAsyncThunk<
  AdminToolIngredient,
  UpdateIngredientMetadataPayload,
  Redux
>('adminToolsIngredients/updateMetadata', async ({ id, data }, { extra }) => {
  const { data: responseData } = await extra.httpClient.put(`cerebro-product-companies/${id}/metadata`, data)

  return responseData?.data ?? responseData
})

export const getStats = createAsyncThunk('adminToolsIngredients/getStats', async (_, { extra }) => {
  const { data: responseData } = await (extra as Redux['extra']).httpClient.get('cerebro-product-companies/stats')

  return responseData?.data ?? responseData
})

const adminToolsIngredientsSlice = createSlice({
  name: 'adminToolsIngredients',
  initialState,
  reducers: {
    clearAdminToolIngredientsFeedback: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminToolIngredients.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchAdminToolIngredients.fulfilled, (state, action) => {
      state.loading = false
      state.ingredients = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(fetchAdminToolIngredients.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message || 'Failed to fetch ingredients'
    })

    builder.addCase(updateAdminToolIngredientMetadata.pending, state => {
      state.updating = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateAdminToolIngredientMetadata.fulfilled, (state, action) => {
      state.updating = false
      state.success = 'Ingredient updated successfully'
      state.ingredients = state.ingredients.map(item => (item.id === action.payload.id ? action.payload : item))
    })
    builder.addCase(updateAdminToolIngredientMetadata.rejected, (state, action) => {
      state.updating = false
      state.error = action.error.message || 'Failed to update ingredient'
    })

    builder.addCase(getStats.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getStats.fulfilled, (state, action) => {
      state.loading = false
      state.stats = action.payload
    })
    builder.addCase(getStats.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message || 'Failed to fetch stats'
    })
  }
})

export const { clearAdminToolIngredientsFeedback } = adminToolsIngredientsSlice.actions

export default adminToolsIngredientsSlice.reducer
